import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faTrash, faSave, faSpinner, faBalanceScale } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { kgPerMetre, formatKg } from '../../utils/coilMath';
import { validateCoilRow, isBlankCoilRow } from '../../utils/validate';

/**
 * CoilEntrySheet
 *
 * The capture surface for coils arriving off a truck: one row per physical coil,
 * typed by somebody standing next to the weighbridge. It is a RIGHT-SIDE DRAWER, not a
 * nested modal — the purchase screens that open it are already a modal (z-50) over a
 * modal (z-[60]), and a third centred layer is unmanageable. A drawer slides in beside
 * whatever is underneath instead of burying it.
 *
 * It is OFFLINE. Nothing here talks to the API: the rows are handed back through
 * onSave and the parent decides when (and whether) to persist them. That keeps the same
 * sheet usable for a manual ledger add, which POSTs immediately, and for a purchase
 * receipt, which holds the coils until the whole receipt is confirmed.
 *
 * Props:
 * - isOpen: whether the drawer is visible
 * - onClose: close without handing anything back
 * - initialCoils: rows to seed with (re-opening an already-captured line)
 * - productName: shown in the header so the operator knows which line they are filling
 * - unitLabel: unit for the expected quantity (kg for every line the app bills)
 * - expectedQuantity: the line's expected quantity, for the running-total comparison
 * - onSave: (coils) => void | false | Promise<void|false>. Returning false keeps the
 *           drawer open with the rows intact — thirty hand-typed coils must not vanish
 *           because the parent's save failed.
 */

/** Today in IST as YYYY-MM-DD — the same trick the purchase challan screens use. */
const todayIso = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

const CoilEntrySheet = ({
  isOpen,
  onClose,
  initialCoils = [],
  productName,
  unitLabel,
  expectedQuantity = null,
  onSave,
}) => {
  const { t } = useTranslation();

  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pendingFocusKey, setPendingFocusKey] = useState(null);

  // Row keys are local and never leave the component — the API knows nothing about them.
  const rowKeyRef = useRef(0);
  const batchInputRefs = useRef({});

  const nextKey = () => {
    rowKeyRef.current += 1;
    return `row-${rowKeyRef.current}`;
  };

  // A fresh row inherits colour, mill ref and date from the row above it: a truck is
  // usually one colour from one mill on one day, and re-typing that thirty times is how
  // typos get in.
  const blankRow = (from = null) => ({
    key: nextKey(),
    batch_number: '',
    weight_kg: '',
    length_m: '',
    colour: from?.colour || '',
    supplier_batch_ref: from?.supplier_batch_ref || '',
    received_date: from?.received_date || todayIso(),
  });

  // Seeds from this sheet's own payload shape, and tolerates a coil straight off the API
  // (original_weight_kg / original_length_m) so re-opening either one works.
  const toRow = (coil) => ({
    key: nextKey(),
    batch_number: coil?.batch_number ?? '',
    weight_kg: coil?.weight_kg ?? coil?.original_weight_kg ?? '',
    length_m: coil?.length_m ?? coil?.original_length_m ?? '',
    colour: coil?.colour ?? '',
    supplier_batch_ref: coil?.supplier_batch_ref ?? '',
    received_date: coil?.received_date || todayIso(),
  });

  // Seed on open only. Re-seeding while open would wipe rows mid-typing.
  useEffect(() => {
    if (!isOpen) return;
    const seeded = (initialCoils || []).map(toRow);
    const initial = seeded.length > 0 ? seeded : [blankRow()];
    batchInputRefs.current = {};
    setRows(initial);
    setErrors({});
    setFormError(null);
    setSaving(false);
    // Land the caret in the first batch field so typing can start straight away.
    setPendingFocusKey(initial[0].key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Move the caret to whichever batch field was just queued up by Enter or Add.
  useEffect(() => {
    if (!pendingFocusKey) return;
    batchInputRefs.current[pendingFocusKey]?.focus();
    setPendingFocusKey(null);
  }, [pendingFocusKey, rows]);

  // Blank-row detection and per-row validation live in utils/validate so this sheet and
  // the product-size opening-stock block enforce ONE set of rules. They used to be local
  // to this file; a second editor copying them is how the two would have drifted apart.
  const isBlankRow = isBlankCoilRow;
  const validateRow = validateCoilRow;

  const handleFieldChange = (key, field, value) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)));
    setErrors((prev) => {
      if (!prev[key]?.[field]) return prev;
      const rowErrors = { ...prev[key] };
      delete rowErrors[field];
      return { ...prev, [key]: rowErrors };
    });
    setFormError(null);
  };

  const addRow = (from = null) => {
    const row = blankRow(from);
    setRows((prev) => [...prev, row]);
    setPendingFocusKey(row.key);
  };

  const removeRow = (key) => {
    setRows((prev) => {
      const next = prev.filter((row) => row.key !== key);
      // Never leave an empty sheet — there is always something to type into.
      return next.length > 0 ? next : [blankRow()];
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  /**
   * Enter commits the row and jumps to the next one's batch field, adding a row when
   * this was the last. A bad row keeps the caret where it is and shows why.
   */
  const handleRowKeyDown = (event, index) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    const row = rows[index];
    const rowErrors = validateRow(row, index, rows);
    if (Object.keys(rowErrors).length > 0) {
      setErrors((prev) => ({ ...prev, [row.key]: rowErrors }));
      return;
    }

    setErrors((prev) => ({ ...prev, [row.key]: {} }));

    if (index === rows.length - 1) {
      addRow(row);
    } else {
      setPendingFocusKey(rows[index + 1].key);
    }
  };

  const activeRows = useMemo(() => rows.filter((row) => !isBlankRow(row)), [rows]);

  const totalWeight = useMemo(() => activeRows.reduce((sum, row) => {
    const weight = parseFloat(row.weight_kg);
    return Number.isFinite(weight) ? sum + Math.round(weight) : sum;
  }, 0), [activeRows]);

  // The expected quantity is what the order line asked for; the difference chip tells
  // the operator whether the truck matched it before they commit the coils.
  const expected = Number.isFinite(parseFloat(expectedQuantity)) ? Math.round(parseFloat(expectedQuantity)) : null;
  const difference = expected === null ? 0 : totalWeight - expected;

  const handleSave = async () => {
    if (activeRows.length === 0) {
      setFormError(t('coils.errors.noCoilsEntered'));
      return;
    }

    const nextErrors = {};
    let valid = true;
    rows.forEach((row, index) => {
      if (isBlankRow(row)) return;
      const rowErrors = validateRow(row, index, rows);
      nextErrors[row.key] = rowErrors;
      if (Object.keys(rowErrors).length > 0) valid = false;
    });
    setErrors(nextErrors);
    if (!valid) {
      setFormError(null);
      return;
    }

    // Shaped exactly like the POST /coils body minus warehouse_id / product_size_id,
    // which only the parent knows.
    const payload = activeRows.map((row) => ({
      batch_number: String(row.batch_number).trim(),
      supplier_batch_ref: String(row.supplier_batch_ref).trim() || null,
      colour: String(row.colour).trim() || null,
      received_date: row.received_date,
      weight_kg: Math.round(parseFloat(row.weight_kg)),
      length_m: parseFloat(row.length_m),
    }));

    setSaving(true);
    try {
      const result = await onSave?.(payload);
      if (result === false) return; // parent failed to persist — keep the typing
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const inputStyle = (hasError) => ({
    backgroundColor: UI_COLORS.FORMS.INPUT.background,
    borderColor: hasError ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
    color: UI_COLORS.FORMS.INPUT.text,
  });

  const labelClass = 'block text-xs font-semibold mb-1';
  const inputClass = 'w-full px-3 py-2 rounded-lg border-2 text-sm outline-none';

  return (
    // No backdrop-click close: this drawer holds unsaved typing, and one stray click
    // beside it must not throw away a truckload of coils.
    <div
      className="fixed inset-0 z-[70] flex justify-end"
      style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="h-full w-full max-w-3xl flex flex-col shadow-2xl"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <div>
            <h2 className="text-xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {t('coils.entry.title')}
            </h2>
            {productName && (
              <p className="text-sm mt-1 font-medium" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {productName}
              </p>
            )}
            <p className="text-xs mt-1" style={{ color: UI_COLORS.TEXT.MUTED }}>
              {t('coils.entry.subtitle')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="p-2 rounded-lg transition-all duration-200 hover:scale-110"
            style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}
            aria-label={t('common.buttons.close')}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Rows */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {formError && (
            <div
              className="p-3 text-sm rounded-xl"
              style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1), color: UI_COLORS.STATUS.ERROR }}
            >
              {formError}
            </div>
          )}

          {rows.map((row, index) => {
            const rowErrors = errors[row.key] || {};
            // Live kg/m from this coil's own weight and length. Every metre figure
            // downstream is derived from this one number, so it is on screen while the
            // operator types rather than discovered later on a picking list.
            const perMetre = kgPerMetre({ original_weight_kg: row.weight_kg, original_length_m: row.length_m });

            return (
              <div
                key={row.key}
                className="rounded-xl border p-3"
                style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: UI_COLORS.BACKGROUNDS.SECTION }}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase tracking-wide" style={{ color: UI_COLORS.TEXT.MUTED }}>
                    {t('coils.entry.coilNumber', { number: index + 1 })}
                  </span>

                  <div className="flex items-center gap-2">
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold"
                      style={{
                        backgroundColor: colorHelpers.withOpacity(
                          perMetre > 0 ? UI_COLORS.STATUS.INFO : UI_COLORS.TEXT.MUTED, 0.12
                        ),
                        color: perMetre > 0 ? UI_COLORS.STATUS.INFO : UI_COLORS.TEXT.MUTED,
                      }}
                    >
                      <FontAwesomeIcon icon={faBalanceScale} className="text-[10px]" />
                      {perMetre > 0
                        ? t('coils.entry.computedKgPerMetre', { value: perMetre.toFixed(3) })
                        : t('coils.entry.awaitingKgPerMetre')}
                    </span>

                    <button
                      type="button"
                      onClick={() => removeRow(row.key)}
                      className="p-1.5 rounded-lg transition-all duration-200 hover:scale-110"
                      style={{ color: UI_COLORS.STATUS.ERROR, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1) }}
                      aria-label={t('coils.entry.removeRow')}
                      title={t('coils.entry.removeRow')}
                    >
                      <FontAwesomeIcon icon={faTrash} className="text-xs" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  <div className="col-span-2">
                    <label className={labelClass} style={{ color: UI_COLORS.FORMS.LABEL }}>
                      {t('coils.entry.fields.batchNumber')} {t('common.symbols.required')}
                    </label>
                    <input
                      ref={(el) => { batchInputRefs.current[row.key] = el; }}
                      type="text"
                      value={row.batch_number}
                      onChange={(e) => handleFieldChange(row.key, 'batch_number', e.target.value)}
                      onKeyDown={(e) => handleRowKeyDown(e, index)}
                      className={inputClass}
                      style={inputStyle(rowErrors.batch_number)}
                    />
                    {rowErrors.batch_number && (
                      <div className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{rowErrors.batch_number}</div>
                    )}
                  </div>

                  <div>
                    <label className={labelClass} style={{ color: UI_COLORS.FORMS.LABEL }}>
                      {t('coils.entry.fields.weightKg')} {t('common.symbols.required')}
                    </label>
                    <input
                      type="number"
                      inputMode="numeric"
                      step="1"
                      min="0"
                      value={row.weight_kg}
                      onChange={(e) => handleFieldChange(row.key, 'weight_kg', e.target.value)}
                      onKeyDown={(e) => handleRowKeyDown(e, index)}
                      className={inputClass}
                      style={inputStyle(rowErrors.weight_kg)}
                    />
                    {rowErrors.weight_kg && (
                      <div className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{rowErrors.weight_kg}</div>
                    )}
                  </div>

                  <div>
                    <label className={labelClass} style={{ color: UI_COLORS.FORMS.LABEL }}>
                      {t('coils.entry.fields.lengthM')} {t('common.symbols.required')}
                    </label>
                    <input
                      type="number"
                      inputMode="decimal"
                      step="0.001"
                      min="0"
                      value={row.length_m}
                      onChange={(e) => handleFieldChange(row.key, 'length_m', e.target.value)}
                      onKeyDown={(e) => handleRowKeyDown(e, index)}
                      className={inputClass}
                      style={inputStyle(rowErrors.length_m)}
                    />
                    {rowErrors.length_m && (
                      <div className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{rowErrors.length_m}</div>
                    )}
                  </div>

                  <div>
                    <label className={labelClass} style={{ color: UI_COLORS.FORMS.LABEL }}>
                      {t('coils.entry.fields.colour')}
                    </label>
                    <input
                      type="text"
                      value={row.colour}
                      onChange={(e) => handleFieldChange(row.key, 'colour', e.target.value)}
                      onKeyDown={(e) => handleRowKeyDown(e, index)}
                      className={inputClass}
                      style={inputStyle(false)}
                    />
                  </div>

                  <div>
                    <label className={labelClass} style={{ color: UI_COLORS.FORMS.LABEL }}>
                      {t('coils.entry.fields.receivedDate')} {t('common.symbols.required')}
                    </label>
                    <input
                      type="date"
                      value={row.received_date}
                      onChange={(e) => handleFieldChange(row.key, 'received_date', e.target.value)}
                      onKeyDown={(e) => handleRowKeyDown(e, index)}
                      className={inputClass}
                      style={inputStyle(rowErrors.received_date)}
                    />
                    {rowErrors.received_date && (
                      <div className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{rowErrors.received_date}</div>
                    )}
                  </div>

                  <div className="col-span-2 md:col-span-6">
                    <label className={labelClass} style={{ color: UI_COLORS.FORMS.LABEL }}>
                      {t('coils.entry.fields.supplierBatchRef')}
                    </label>
                    <input
                      type="text"
                      value={row.supplier_batch_ref}
                      onChange={(e) => handleFieldChange(row.key, 'supplier_batch_ref', e.target.value)}
                      onKeyDown={(e) => handleRowKeyDown(e, index)}
                      className={inputClass}
                      style={inputStyle(false)}
                    />
                  </div>
                </div>
              </div>
            );
          })}

          <button
            type="button"
            onClick={() => addRow(rows[rows.length - 1])}
            className="w-full py-2.5 rounded-xl text-sm font-semibold border-2 border-dashed transition-all duration-200"
            style={{ borderColor: UI_COLORS.BORDERS.MEDIUM, color: UI_COLORS.TEXT.SECONDARY }}
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            {t('coils.entry.addAnother')}
          </button>
        </div>

        {/* Running total */}
        <div className="px-6 py-4 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {t('coils.entry.runningTotal', { count: activeRows.length, weight: formatKg(totalWeight) })}
              </div>
              {expected !== null && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>
                    {t('coils.entry.expected', {
                      quantity: formatKg(expected),
                      unit: unitLabel || t('common.units.kg'),
                    })}
                  </span>
                  <span
                    className="px-2 py-0.5 rounded-full text-xs font-bold"
                    style={{
                      backgroundColor: colorHelpers.withOpacity(
                        difference === 0 ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.WARNING, 0.12
                      ),
                      color: difference === 0 ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.WARNING,
                    }}
                  >
                    {difference === 0 && t('coils.entry.difference.exact')}
                    {difference < 0 && t('coils.entry.difference.short', { amount: formatKg(-difference) })}
                    {difference > 0 && t('coils.entry.difference.over', { amount: formatKg(difference) })}
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="px-5 py-2.5 rounded-xl font-medium transition-all duration-200"
                style={{
                  backgroundColor: 'transparent',
                  color: UI_COLORS.TEXT.MUTED,
                  border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
                }}
              >
                {t('common.buttons.cancel')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2.5 rounded-xl font-semibold flex items-center transition-all duration-200 disabled:opacity-60"
                style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}
              >
                <FontAwesomeIcon icon={saving ? faSpinner : faSave} spin={saving} className="mr-2" />
                {saving ? t('common.buttons.saving') : t('coils.entry.save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoilEntrySheet;
